import usePlan from './usePlan'
import useBadges from './useBadges'
//import { sort } from 'fast-sort'
import useUtils from './useUtils'

/**
 * This composition function exposes data and functionality around sort.
 */
export default function useSort(profile, context = { type: 'doctor' }) {

  //This map defines the CPN sort strings for the different types of sort selections. The 
  //Selections are identified by -p for practitioners and -l for locations since the sort
  //can be different for practitioners and locations.
  const cpnSortTypeMap = {
    "relevancy-p": 'distanceInMiles,lastName,firstName,middleName',
    "distance-p": 'distanceInMiles,lastName,firstName,middleName',
    "name-p": 'lastName,firstName,middleName',
    "specialty-p": 'practicingSpecialty,distanceInMiles,lastName,firstName,middleName',
    "tier-p": 'tier,distanceInMiles,lastName,firstName,middleName',
    "quality-p": '-quality,cost,distanceInMiles,lastName,firstName,middleName',
    "cost-p": 'cost,-quality,distanceInMiles,lastName,firstName,middleName',

    "relevancy-l": 'distanceInMiles,name',
    "topValue-d-l": 'betterChoiceCategory,distanceInMiles,name',
    "topValue-nd-l": 'betterChoiceCategory,name',
    "distance-l": 'distanceInMiles,name',
    "name-l": 'name',
    "quality-l": '-quality,distanceInMiles,name',
    "cost-l": 'cost,-quality,distanceInMiles,name',
    "tier-l": 'tier,distanceInMiles,name'
  }

  const { getPlan } = usePlan(profile, context)
  const { getBadgeTypesInResults } = useBadges()
  const { clone } = useUtils()

  const hasPropertyAndData = (propertyName, searchResults) => {
    const result = searchResults.find((r) => r[propertyName])
    return result ? true : false
  }

  const getPractitionerSortOptions = (searchResults) => {
    if (!searchResults) return []

    const options = new Map()

    const hasRelevance = fns.hasPropertyAndData('relevancyScore', searchResults)
    const hasDistance = fns.hasPropertyAndData('distanceInMiles', searchResults)
    const hasQualityRating = fns.hasPropertyAndData('qualityRating', searchResults)
    const hasCostRating = fns.hasPropertyAndData('costRating', searchResults)
    const hasTier = fns.hasPropertyAndData('tier', searchResults)

    if ( hasRelevance ) {
      options.set('Relevance', { label: 'Relevance', value: 'relevancy-p' })
    }

    if (hasDistance) options.set('Distance', { label: 'Distance', value: 'distance-p' })

    options.set('Name', { label: 'Name A-Z', value: 'name-p' })
    options.set('Specialty', { label: 'Specialty', value: 'specialty-p' })
    
    if (hasTier && getPlan().isTiered)
      options.set('Tier', { label: `${getPlan().tierLabel}`, value: 'tier-p' })

    if (profile.showCostQuality && hasCostRating)
      options.set('Cost', { label: 'Cost', value: 'cost-p' })

    if (profile.showCostQuality && hasQualityRating)
      options.set('Quality', { label: 'Quality', value: 'quality-p' })

    return Array.from(options.values())
  }

  const getLocationSortOptions = (searchResults) => {
    if (!searchResults) return []

    const options = new Map()
    const badgeTypes = getBadgeTypesInResults(searchResults)
    const hasHighValueNetwork = badgeTypes.includes('HVN')
    const hasDistance = fns.hasPropertyAndData('distanceInMiles', searchResults)
    const hasRelevance = fns.hasPropertyAndData('relevancyScore', searchResults)
    const hasQualityRating = fns.hasPropertyAndData('qualityRating', searchResults)
    const hasCostRating = fns.hasPropertyAndData('costRating', searchResults)
    const hasTier = fns.hasPropertyAndData('tier', searchResults)

    if (hasHighValueNetwork && hasDistance )
      options.set('TopValue', { label: 'Top Value', value: 'topValue-d-l' })

    if (hasHighValueNetwork && !hasDistance) options.set('TopValue', { label: 'Top Value', value: 'topValue-nd-l' })

    if ( hasRelevance ) {
      options.set('Relevance', { label: 'Relevance', value: 'relevancy-l' })
    }

    if (hasDistance){ 
      options.set('Distance', { label: 'Distance', value: 'distance-l' })
    }

    options.set('Name', { label: 'Name A-Z', value: 'name-l' })

    if (hasTier && getPlan().isTiered) options.set('Tier', { label: `${getPlan().tierLabel}`, value: 'tier-l' })

    if (profile.showCostQuality && hasQualityRating)
      options.set('Quality', { label: 'Quality', value: 'quality-l' })

    if (profile.showCostQuality && hasCostRating)
      options.set('Cost', { label: 'Cost', value: 'cost-l' })

    return Array.from(options.values())
  }

  /**
   * Accepts the sort string from the UI and massages it for presentation to CPN.  We have to remove items like price
   * for instance that we have to sort on the app and not by CPN.
   */
  const getCpnSortString = (sortString) => {
    return cpnSortTypeMap[sortString]
  }

  /**
   * Execute a secondary sort on results based upon the sort string.  In general, this is used to run sorts that require a
   * sorting with 'price' as a component since price requires costEstimates which are applied after the CPN search and thus
   * cannot be sorted by CPN.
   *
   * Since the data here is structured the same for locations and practitioners, the same sort functions can be
   * applied to each resultset.
   */
  const secondarySort = (results, sortString) => {
    const sortArray = sortString.split(',')

    if ( sortString.startsWith('topValue') ) {
      return topValueSort(results)
    }

    if ( !sortString || sortString.startsWith('relevancy') ) {
      console.log("Executing relevancy sort")
      return relevancySort(results)
    }

    if (sortArray.length > 1 && sortArray[0] == 'betterChoiceCategory') {
      return betterChoiceCategorySort(results)
    }

    return clone(results)
  }

  const betterChoiceCategorySort = (results) => {

    const toSort = clone(results)

    //Sort by betterChoiceCategory (HVN at the top)
    toSort.sort((a,b) => {
        if ( a.betterChoiceCategory === 'HVN' && b.betterChoiceCategory === 'HVN' ) return 0
        if ( a.betterChoiceCategory == 'HVN'  && b.betterChoiceCategory != 'HVN') return -1
        if ( a.betterChoiceCategory != 'HVN' && b.betterChoiceCategory === 'HVN') return 1
        return 0
    })

    return toSort    
  }

  const topValueSort = (results) => { 
    const toSort = clone(results)
    toSort.sort((a,b) => {
      if ( a.betterChoiceCategory === 'HVN' && b.betterChoiceCategory === 'HVN' ) return 0
      if ( a.betterChoiceCategory == 'HVN'  && b.betterChoiceCategory != 'HVN') return -1
      if ( a.betterChoiceCategory != 'HVN' && b.betterChoiceCategory === 'HVN') return 1
      return 0
   })
   return toSort
  }


  const relevancySort = (results) => {
   
    // Group results by relevancyScore. this function returns an object with keys as relevancyScore and values as an array of results
    const groupedByRelevancy = [...results].reduce((acc, result) => {
      if (!acc[result.relevancyScore]) {
      acc[result.relevancyScore] = [];
      }
      acc[result.relevancyScore].push(result);
      return acc;
    }, {});


    // Sort each group by costRating.value (1 first) and then by distanceInMiles
    /* Object.entries is used here to iterate over the key-value pairs of the object, allowing us to process both the keys and values simultaneously.
        ### Explanation:
        If `groupedByRelevancy` is an object, calling `Object.entries(groupedByRelevancy)` will transform it into an array where:
        - Each element is an array `[key, value]`.
        - `key` is a property name (or key) from the object.
        - `value` is the corresponding value for that key.

        ### Example:
        Suppose `groupedByRelevancy` looks like this:
        const groupedByRelevancy = {
          high: [1, 2, 3],
          medium: [4, 5],
          low: [6]
        };
        When you call `Object.entries(groupedByRelevancy)`, it will return:
        [
          ['high', [1, 2, 3]],
          ['medium', [4, 5]],
          ['low', [6]]
        ]*/

    const sortedGroups = Object.entries(groupedByRelevancy)
      .sort(([scoreA], [scoreB]) => parseFloat(scoreB) - parseFloat(scoreA)) // Ensure proper descending order of relevancyScore
      .map(([, group]) => {
      const costRatingWithValueOfOne = group.filter(r => r.costRating?.value === 1);
      costRatingWithValueOfOne.sort((a, b) => a.distanceInMiles - b.distanceInMiles);
      const otherResults = group.filter(r => r.costRating?.value !== 1);
      otherResults.sort((a, b) => a.distanceInMiles - b.distanceInMiles);
      return [...costRatingWithValueOfOne, ...otherResults];
      });

    // Flatten the sorted groups into a single array
    const sortedResults = sortedGroups.flat();

    return sortedResults;

}

  /**
   * Return the correct list of sort selections based upon the 'type' in context.
   */
  const getSortOptions = (searchResults) => {
    return context.type == 'doctor' || context.type == 'dentist'
      ? fns.getPractitionerSortOptions(searchResults)
      : fns.getLocationSortOptions(searchResults)
  }

  const fns = {
    hasPropertyAndData,
    getPractitionerSortOptions,
    getLocationSortOptions,
    getSortOptions,
    getCpnSortString,
    secondarySort,
    relevancySort
  }

  return fns
}